import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { BarChart3, Loader2, PieChart, TrendingUp } from "lucide-react";
import { DynamicChart } from "./DynamicChart";
import { toast } from "sonner";
import {
  buildRecipes,
  listDataSources,
  loadSourceRows,
  type ChartRecipe,
  type SourceOption,
} from "@/lib/dashboard-data";
import { useSavedCharts } from "@/hooks/useSavedCharts";

type TemplateId = "trends" | "breakdown" | "performance";

const TEMPLATES: {
  id: TemplateId;
  title: string;
  description: string;
  icon: any;
}[] = [
  {
    id: "trends",
    title: "Trends over time",
    description: "See how your numbers move across dates or months.",
    icon: TrendingUp,
  },
  {
    id: "breakdown",
    title: "Breakdown by category",
    description: "See which categories make up the biggest share.",
    icon: PieChart,
  },
  {
    id: "performance",
    title: "Compare performance",
    description: "Compare averages side by side across groups.",
    icon: BarChart3,
  },
];

export const DashboardTemplates = () => {
  const { saveChart } = useSavedCharts();
  const [sources, setSources] = useState<SourceOption[]>([]);
  const [sourceId, setSourceId] = useState("");
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<TemplateId | null>(null);
  const [recipes, setRecipes] = useState<ChartRecipe[]>([]);

  useEffect(() => {
    listDataSources().then(setSources).catch(() => setSources([]));
  }, []);

  const chooseSource = async (id: string) => {
    setSourceId(id);
    setRecipes([]);
    setSelected(null);
    setLoading(true);
    try {
      const loaded = await loadSourceRows(id);
      setRows(loaded);
      toast.success(`Loaded ${loaded.length} rows`);
    } catch (e: any) {
      setRows([]);
      toast.error(e?.message || "Could not load that data");
    } finally {
      setLoading(false);
    }
  };

  const applyTemplate = (id: TemplateId) => {
    setSelected(id);
    const built = buildRecipes(rows, id);
    setRecipes(built);
    if (!built.length) {
      toast.error("Your data doesn't have the columns this template needs.");
    }
  };

  const saveAll = async () => {
    const sourceName = sources.find((s) => s.id === sourceId)?.label ?? null;
    for (const r of recipes) {
      await saveChart.mutateAsync({
        title: r.title,
        chartType: r.chartType,
        xField: r.xField,
        yField: r.yField,
        aggregation: r.aggregation,
        data: r.data,
        origin: "template",
        sourceType: sourceId.split(":")[0],
        sourceName,
        sourceId,
      });
    }
    toast.success(`Saved ${recipes.length} chart(s) to your dashboard`);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Ready-made dashboards</h2>
          <p className="text-sm text-muted-foreground">
            Pick your data, then pick a layout. Every chart is built from your real rows.
          </p>
        </div>

        <div className="max-w-md">
          <Select value={sourceId} onValueChange={chooseSource}>
            <SelectTrigger>
              <SelectValue placeholder={sources.length ? "Choose your data" : "No data yet"} />
            </SelectTrigger>
            <SelectContent>
              {sources.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Reading your data...
          </div>
        )}

        {!sources.length && (
          <Alert>
            <AlertDescription>
              Upload a file or run a workflow first — templates only build charts from your own data.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {TEMPLATES.map((t) => (
            <Card
              key={t.id}
              onClick={() => rows.length && applyTemplate(t.id)}
              className={`p-4 transition-colors border-2 ${
                rows.length ? "cursor-pointer hover:bg-accent" : "opacity-60"
              } ${selected === t.id ? "border-primary" : "border-transparent"}`}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <t.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium">{t.title}</h3>
                  <p className="text-sm text-muted-foreground">{t.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </Card>

      {recipes.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold">Preview</h3>
              <Badge variant="default">{recipes.length} charts</Badge>
            </div>
            <Button onClick={saveAll} disabled={saveChart.isPending}>
              {saveChart.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save to dashboard
            </Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {recipes.map((r) => (
              <DynamicChart
                key={r.title}
                config={{
                  id: r.title,
                  type: r.chartType,
                  title: r.title,
                  data: r.data,
                  xAxis: r.xField,
                  yAxis: r.yField,
                }}
                data={r.data}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
